/**
 * Mock Customer Data
 * 50+ realistic individual taxpayer customers with varied profiles
 */

import type {
  IndividualCustomer,
  Assessment,
  AssignedCA,
  Address,
} from '@/types/customer'

// ============================================================================
// ASSIGNED CAs (Team Members)
// ============================================================================

export const mockCAs: AssignedCA[] = [
  { id: 'ca-001', firstName: 'Rajesh', lastName: 'Kumar', email: 'rajesh.kumar@taxhive.in', initials: 'RK', customerCount: 18 },
  { id: 'ca-002', firstName: 'Priya', lastName: 'Sharma', email: 'priya.sharma@taxhive.in', initials: 'PS', customerCount: 15 },
  { id: 'ca-003', firstName: 'Amit', lastName: 'Patel', email: 'amit.patel@taxhive.in', initials: 'AP', customerCount: 12 },
  { id: 'ca-004', firstName: 'Neha', lastName: 'Gupta', email: 'neha.gupta@taxhive.in', initials: 'NG', customerCount: 10 },
]

// ============================================================================
// SAMPLE ADDRESSES BY STATE
// ============================================================================

const addresses: Record<string, Address[]> = {
  MH: [
    { line1: '101, Oberoi Towers', line2: 'Nariman Point', city: 'Mumbai', state: 'Maharashtra', pincode: '400021', country: 'India' },
    { line1: '45, Hiranandani Gardens', line2: 'Powai', city: 'Mumbai', state: 'Maharashtra', pincode: '400076', country: 'India' },
    { line1: '12, Shivaji Nagar', city: 'Pune', state: 'Maharashtra', pincode: '411005', country: 'India' },
    { line1: '78, Koregaon Park', city: 'Pune', state: 'Maharashtra', pincode: '411001', country: 'India' },
    { line1: '33, FC Road', city: 'Pune', state: 'Maharashtra', pincode: '411004', country: 'India' },
  ],
  KA: [
    { line1: '23, Koramangala 4th Block', city: 'Bangalore', state: 'Karnataka', pincode: '560034', country: 'India' },
    { line1: '56, Indiranagar', line2: 'CMH Road', city: 'Bangalore', state: 'Karnataka', pincode: '560038', country: 'India' },
    { line1: '89, Whitefield', line2: 'ITPL Main Road', city: 'Bangalore', state: 'Karnataka', pincode: '560066', country: 'India' },
    { line1: '14, HSR Layout', line2: 'Sector 2', city: 'Bangalore', state: 'Karnataka', pincode: '560102', country: 'India' },
    { line1: '67, Jayanagar', city: 'Bangalore', state: 'Karnataka', pincode: '560041', country: 'India' },
  ],
  DL: [
    { line1: '15, Defence Colony', city: 'New Delhi', state: 'Delhi', pincode: '110024', country: 'India' },
    { line1: '42, Vasant Kunj', line2: 'Sector C', city: 'New Delhi', state: 'Delhi', pincode: '110070', country: 'India' },
    { line1: '8, Greater Kailash II', city: 'New Delhi', state: 'Delhi', pincode: '110048', country: 'India' },
    { line1: '91, Dwarka', line2: 'Sector 12', city: 'New Delhi', state: 'Delhi', pincode: '110075', country: 'India' },
    { line1: '27, Connaught Place', city: 'New Delhi', state: 'Delhi', pincode: '110001', country: 'India' },
  ],
  GJ: [
    { line1: '34, CG Road', city: 'Ahmedabad', state: 'Gujarat', pincode: '380009', country: 'India' },
    { line1: '56, Satellite', city: 'Ahmedabad', state: 'Gujarat', pincode: '380015', country: 'India' },
    { line1: '78, Navrangpura', city: 'Ahmedabad', state: 'Gujarat', pincode: '380009', country: 'India' },
    { line1: '12, Athwalines', city: 'Surat', state: 'Gujarat', pincode: '395001', country: 'India' },
    { line1: '45, Alkapuri', city: 'Vadodara', state: 'Gujarat', pincode: '390007', country: 'India' },
  ],
  TN: [
    { line1: '23, T Nagar', city: 'Chennai', state: 'Tamil Nadu', pincode: '600017', country: 'India' },
    { line1: '67, Adyar', line2: 'Gandhi Nagar', city: 'Chennai', state: 'Tamil Nadu', pincode: '600020', country: 'India' },
    { line1: '89, Anna Nagar', city: 'Chennai', state: 'Tamil Nadu', pincode: '600040', country: 'India' },
    { line1: '45, RS Puram', city: 'Coimbatore', state: 'Tamil Nadu', pincode: '641002', country: 'India' },
    { line1: '12, Lawley Road', city: 'Coimbatore', state: 'Tamil Nadu', pincode: '641001', country: 'India' },
  ],
  TG: [
    { line1: '34, Jubilee Hills', city: 'Hyderabad', state: 'Telangana', pincode: '500033', country: 'India' },
    { line1: '78, Banjara Hills', city: 'Hyderabad', state: 'Telangana', pincode: '500034', country: 'India' },
    { line1: '12, Gachibowli', city: 'Hyderabad', state: 'Telangana', pincode: '500032', country: 'India' },
    { line1: '56, Hitech City', city: 'Hyderabad', state: 'Telangana', pincode: '500081', country: 'India' },
    { line1: '23, Secunderabad', city: 'Secunderabad', state: 'Telangana', pincode: '500003', country: 'India' },
  ],
  UP: [
    { line1: '45, Hazratganj', city: 'Lucknow', state: 'Uttar Pradesh', pincode: '226001', country: 'India' },
    { line1: '67, Gomti Nagar', city: 'Lucknow', state: 'Uttar Pradesh', pincode: '226010', country: 'India' },
    { line1: '23, Civil Lines', city: 'Kanpur', state: 'Uttar Pradesh', pincode: '208001', country: 'India' },
    { line1: '89, Sector 62', city: 'Noida', state: 'Uttar Pradesh', pincode: '201301', country: 'India' },
    { line1: '12, Crossing Republik', city: 'Ghaziabad', state: 'Uttar Pradesh', pincode: '201016', country: 'India' },
  ],
  RJ: [
    { line1: '34, C-Scheme', city: 'Jaipur', state: 'Rajasthan', pincode: '302001', country: 'India' },
    { line1: '56, Malviya Nagar', city: 'Jaipur', state: 'Rajasthan', pincode: '302017', country: 'India' },
    { line1: '78, Vaishali Nagar', city: 'Jaipur', state: 'Rajasthan', pincode: '302021', country: 'India' },
    { line1: '12, Sardarpura', city: 'Jodhpur', state: 'Rajasthan', pincode: '342001', country: 'India' },
    { line1: '45, Lake City', city: 'Udaipur', state: 'Rajasthan', pincode: '313001', country: 'India' },
  ],
  WB: [
    { line1: '23, Salt Lake', line2: 'Sector V', city: 'Kolkata', state: 'West Bengal', pincode: '700091', country: 'India' },
    { line1: '67, Park Street', city: 'Kolkata', state: 'West Bengal', pincode: '700016', country: 'India' },
    { line1: '89, New Town', city: 'Kolkata', state: 'West Bengal', pincode: '700156', country: 'India' },
    { line1: '45, Ballygunge', city: 'Kolkata', state: 'West Bengal', pincode: '700019', country: 'India' },
    { line1: '12, Alipore', city: 'Kolkata', state: 'West Bengal', pincode: '700027', country: 'India' },
  ],
  KL: [
    { line1: '34, MG Road', city: 'Kochi', state: 'Kerala', pincode: '682016', country: 'India' },
    { line1: '56, Marine Drive', city: 'Kochi', state: 'Kerala', pincode: '682031', country: 'India' },
    { line1: '78, Technopark', city: 'Thiruvananthapuram', state: 'Kerala', pincode: '695581', country: 'India' },
    { line1: '12, Calicut Beach', city: 'Kozhikode', state: 'Kerala', pincode: '673001', country: 'India' },
    { line1: '45, Thrissur Round', city: 'Thrissur', state: 'Kerala', pincode: '680001', country: 'India' },
  ],
}

// ============================================================================
// MOCK ASSESSMENTS GENERATOR
// ============================================================================

function generateAssessments(customerId: string, count: number): Assessment[] {
  const assessments: Assessment[] = []
  const currentYear = 2025
  const statuses: Assessment['status'][] = [
    'not_started', 'documents_pending', 'in_progress', 'under_review', 'filed', 'processed', 'refund_issued', 'demand_raised'
  ]
  const itrTypes: Assessment['itrType'][] = ['ITR-1', 'ITR-2', 'ITR-3', 'ITR-4']

  for (let i = 0; i < count; i++) {
    const fy = currentYear - i - 1
    const fyStr = `${fy}-${(fy + 1).toString().slice(-2)}`
    const ayStr = `${fy + 1}-${(fy + 2).toString().slice(-2)}`
    const status = i === 0 ? statuses[Math.floor(Math.random() * 4)] : statuses[Math.floor(Math.random() * 4) + 4]

    assessments.push({
      id: `asmt-${customerId}-${i + 1}`,
      customerId,
      financialYear: fyStr,
      assessmentYear: ayStr,
      itrType: itrTypes[Math.floor(Math.random() * itrTypes.length)],
      status,
      dueDate: `${fy + 1}-07-31`,
      filedDate: status === 'filed' || status === 'processed' || status === 'refund_issued' ? `${fy + 1}-07-${Math.floor(Math.random() * 20) + 1}` : undefined,
      acknowledgeNumber: status === 'filed' || status === 'processed' || status === 'refund_issued' ? `ACK${Date.now()}${Math.floor(Math.random() * 1000)}` : undefined,
      grossIncome: Math.floor(Math.random() * 5000000) + 500000,
      taxableIncome: Math.floor(Math.random() * 4000000) + 300000,
      taxPaid: Math.floor(Math.random() * 500000) + 50000,
      refundAmount: status === 'refund_issued' ? Math.floor(Math.random() * 50000) + 5000 : undefined,
      demandAmount: status === 'demand_raised' ? Math.floor(Math.random() * 100000) + 10000 : undefined,
      createdDate: `${fy + 1}-04-01`,
      lastUpdated: new Date().toISOString(),
    })
  }

  return assessments
}

// ============================================================================
// MOCK CUSTOMERS DATA (55 Customers)
// ============================================================================

export const mockCustomers: IndividualCustomer[] = [
  // Salaried - IT Professionals (10)
  {
    id: 'cust-001',
    fullName: 'Ramesh Kumar Verma',
    pan: 'ABCPV1234K',
    aadhaar: '234567890123',
    dateOfBirth: '1985-03-15',
    gender: 'male',
    emailPrimary: 'ramesh.verma@techcorp.in',
    mobilePrimary: '9876543210',
    whatsappNumber: '9876543210',
    residentialAddress: addresses.KA[0],
    occupation: 'salaried',
    employerName: 'Tech Mahindra',
    annualIncomeRange: '10-25L',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 185000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2022-04-15',
    lastUpdated: '2025-01-10',
    totalAssessments: 4,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-001', 4),
    properties: [
      { id: 'prop-001', type: 'residential', address: addresses.KA[0], estimatedValue: 12000000, isRented: false, purchaseDate: '2019-06-15', purchaseValue: 8500000 }
    ],
    bankAccounts: [
      { id: 'bank-001', bankName: 'HDFC Bank', accountNumber: '50100123456789', ifscCode: 'HDFC0001234', accountType: 'salary', isPrimary: true }
    ],
  },
  {
    id: 'cust-002',
    fullName: 'Priya Sharma',
    pan: 'BGHPS5678M',
    aadhaar: '345678901234',
    dateOfBirth: '1990-07-22',
    gender: 'female',
    emailPrimary: 'priya.sharma@infosys.com',
    emailSecondary: 'priyasharma90@gmail.com',
    mobilePrimary: '9765432109',
    mobileSecondary: '9812345678',
    whatsappNumber: '9765432109',
    residentialAddress: addresses.MH[0],
    permanentAddress: addresses.UP[0],
    occupation: 'salaried',
    employerName: 'Infosys Ltd',
    annualIncomeRange: '25-50L',
    hasHouseProperty: false,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 420000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2021-06-20',
    lastUpdated: '2025-01-08',
    totalAssessments: 5,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-002', 5),
    investments: [
      { id: 'inv-001', type: 'mutual_fund', name: 'HDFC Equity Fund', institution: 'HDFC AMC', investedAmount: 500000, currentValue: 680000 },
      { id: 'inv-002', type: 'stocks', name: 'Equity Portfolio', institution: 'Zerodha', investedAmount: 300000, currentValue: 450000 },
    ],
  },
  {
    id: 'cust-003',
    fullName: 'Vikram Singh Rajput',
    pan: 'CJKRS9012N',
    aadhaar: '456789012345',
    dateOfBirth: '1982-11-08',
    gender: 'male',
    emailPrimary: 'vikram.rajput@wipro.com',
    mobilePrimary: '9654321098',
    whatsappNumber: '9654321098',
    residentialAddress: addresses.TG[0],
    occupation: 'salaried',
    employerName: 'Wipro Technologies',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 850000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2020-03-10',
    lastUpdated: '2025-01-05',
    totalAssessments: 6,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-003', 6),
    properties: [
      { id: 'prop-002', type: 'residential', address: addresses.TG[0], estimatedValue: 25000000, isRented: false, purchaseDate: '2018-03-20', purchaseValue: 18000000 },
      { id: 'prop-003', type: 'residential', address: addresses.TG[1], estimatedValue: 15000000, isRented: true, annualRentalIncome: 360000, purchaseDate: '2020-08-15', purchaseValue: 12000000 },
    ],
  },
  {
    id: 'cust-004',
    fullName: 'Ananya Krishnamurthy',
    pan: 'DMNKR3456P',
    dateOfBirth: '1992-04-18',
    gender: 'female',
    emailPrimary: 'ananya.k@google.com',
    mobilePrimary: '9543210987',
    residentialAddress: addresses.KA[1],
    occupation: 'salaried',
    employerName: 'Google India',
    annualIncomeRange: '50L+',
    hasHouseProperty: false,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 1200000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2021-09-05',
    lastUpdated: '2025-01-12',
    totalAssessments: 4,
    pendingAssessments: 2,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-004', 4),
  },
  {
    id: 'cust-005',
    fullName: 'Sanjay Mehta',
    pan: 'EFQMS7890R',
    aadhaar: '567890123456',
    dateOfBirth: '1978-09-25',
    gender: 'male',
    emailPrimary: 'sanjay.mehta@tcs.com',
    mobilePrimary: '9432109876',
    whatsappNumber: '9432109876',
    residentialAddress: addresses.MH[2],
    permanentAddress: addresses.GJ[0],
    occupation: 'salaried',
    employerName: 'TCS',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 380000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2019-07-12',
    lastUpdated: '2025-01-07',
    totalAssessments: 7,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-005', 7),
  },
  {
    id: 'cust-006',
    fullName: 'Kavitha Nair',
    pan: 'FGHNR1234S',
    dateOfBirth: '1988-12-03',
    gender: 'female',
    emailPrimary: 'kavitha.nair@amazon.in',
    mobilePrimary: '9321098765',
    residentialAddress: addresses.KL[0],
    occupation: 'salaried',
    employerName: 'Amazon Development Centre',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 980000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2022-01-20',
    lastUpdated: '2025-01-09',
    totalAssessments: 4,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-006', 4),
  },
  {
    id: 'cust-007',
    fullName: 'Arun Prakash Reddy',
    pan: 'GHIPR5678T',
    dateOfBirth: '1984-06-14',
    gender: 'male',
    emailPrimary: 'arun.reddy@microsoft.com',
    mobilePrimary: '9210987654',
    residentialAddress: addresses.TG[2],
    occupation: 'salaried',
    employerName: 'Microsoft India',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 1450000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2020-11-08',
    lastUpdated: '2025-01-11',
    totalAssessments: 5,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-007', 5),
  },
  {
    id: 'cust-008',
    fullName: 'Deepika Iyer',
    pan: 'HIJIY9012U',
    dateOfBirth: '1991-02-28',
    gender: 'female',
    emailPrimary: 'deepika.iyer@flipkart.com',
    mobilePrimary: '9109876543',
    residentialAddress: addresses.KA[3],
    occupation: 'salaried',
    employerName: 'Flipkart Internet',
    annualIncomeRange: '25-50L',
    hasHouseProperty: false,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 520000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2021-04-18',
    lastUpdated: '2025-01-06',
    totalAssessments: 5,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-008', 5),
  },
  {
    id: 'cust-009',
    fullName: 'Rajiv Kapoor',
    pan: 'IJKKP3456V',
    dateOfBirth: '1976-08-19',
    gender: 'male',
    emailPrimary: 'rajiv.kapoor@accenture.com',
    mobilePrimary: '9098765432',
    residentialAddress: addresses.DL[0],
    occupation: 'salaried',
    employerName: 'Accenture',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 1100000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2018-06-25',
    lastUpdated: '2025-01-04',
    totalAssessments: 8,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-009', 8),
  },
  {
    id: 'cust-010',
    fullName: 'Meera Subramaniam',
    pan: 'JKLMS7890W',
    dateOfBirth: '1987-10-07',
    gender: 'female',
    emailPrimary: 'meera.s@cognizant.com',
    mobilePrimary: '8987654321',
    residentialAddress: addresses.TN[0],
    occupation: 'salaried',
    employerName: 'Cognizant Technology',
    annualIncomeRange: '10-25L',
    hasHouseProperty: false,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 165000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2022-08-30',
    lastUpdated: '2025-01-03',
    totalAssessments: 3,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-010', 3),
  },

  // Self-Employed Professionals (10)
  {
    id: 'cust-011',
    fullName: 'Dr. Suresh Patil',
    pan: 'KLMSP1234X',
    aadhaar: '678901234567',
    dateOfBirth: '1972-05-12',
    gender: 'male',
    emailPrimary: 'dr.suresh.patil@gmail.com',
    mobilePrimary: '8876543210',
    whatsappNumber: '8876543210',
    residentialAddress: addresses.MH[3],
    occupation: 'self_employed',
    employerName: 'Patil Clinic & Hospital',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 1350000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2017-04-10',
    lastUpdated: '2025-01-10',
    totalAssessments: 9,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-011', 9),
  },
  {
    id: 'cust-012',
    fullName: 'Adv. Sneha Desai',
    pan: 'LMNSD5678Y',
    dateOfBirth: '1980-09-23',
    gender: 'female',
    emailPrimary: 'sneha.desai.advocate@gmail.com',
    mobilePrimary: '8765432109',
    residentialAddress: addresses.GJ[1],
    occupation: 'self_employed',
    employerName: 'Desai & Associates',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: true,
    previousYearTaxPaid: 580000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2019-02-15',
    lastUpdated: '2025-01-08',
    totalAssessments: 7,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-012', 7),
  },
  {
    id: 'cust-013',
    fullName: 'CA Mohan Agarwal',
    pan: 'MNOMA9012Z',
    dateOfBirth: '1975-01-30',
    gender: 'male',
    emailPrimary: 'mohan.agarwal.ca@gmail.com',
    mobilePrimary: '8654321098',
    residentialAddress: addresses.RJ[0],
    occupation: 'self_employed',
    employerName: 'Agarwal & Co.',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 620000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2018-08-22',
    lastUpdated: '2025-01-07',
    totalAssessments: 8,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-013', 8),
  },
  {
    id: 'cust-014',
    fullName: 'Arch. Pooja Malhotra',
    pan: 'NOPPM3456A',
    dateOfBirth: '1983-07-16',
    gender: 'female',
    emailPrimary: 'pooja.architect@gmail.com',
    mobilePrimary: '8543210987',
    residentialAddress: addresses.DL[2],
    occupation: 'self_employed',
    employerName: 'Malhotra Design Studio',
    annualIncomeRange: '10-25L',
    hasHouseProperty: false,
    hasCapitalGains: false,
    hasBusinessIncome: true,
    previousYearTaxPaid: 185000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2020-05-18',
    lastUpdated: '2025-01-06',
    totalAssessments: 6,
    pendingAssessments: 2,
    overdueAssessments: 1,
    assessments: generateAssessments('cust-014', 6),
  },
  {
    id: 'cust-015',
    fullName: 'Dr. Rekha Menon',
    pan: 'OPQRM7890B',
    dateOfBirth: '1979-11-05',
    gender: 'female',
    emailPrimary: 'dr.rekha.menon@gmail.com',
    mobilePrimary: '8432109876',
    residentialAddress: addresses.KL[1],
    occupation: 'self_employed',
    employerName: 'Menon Dental Clinic',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: true,
    previousYearTaxPaid: 450000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2019-11-28',
    lastUpdated: '2025-01-05',
    totalAssessments: 6,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-015', 6),
  },
  {
    id: 'cust-016',
    fullName: 'CS Vivek Jain',
    pan: 'PQRVJ1234C',
    dateOfBirth: '1981-04-09',
    gender: 'male',
    emailPrimary: 'vivek.jain.cs@gmail.com',
    mobilePrimary: '8321098765',
    residentialAddress: addresses.MH[4],
    occupation: 'self_employed',
    employerName: 'Jain & Associates CS',
    annualIncomeRange: '10-25L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: true,
    previousYearTaxPaid: 195000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2021-01-15',
    lastUpdated: '2025-01-04',
    totalAssessments: 5,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-016', 5),
  },
  {
    id: 'cust-017',
    fullName: 'Dr. Amit Saxena',
    pan: 'QRSAS5678D',
    dateOfBirth: '1977-08-21',
    gender: 'male',
    emailPrimary: 'dr.amit.saxena@gmail.com',
    mobilePrimary: '8210987654',
    residentialAddress: addresses.UP[1],
    occupation: 'self_employed',
    employerName: 'Saxena Eye Hospital',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 1580000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2016-09-12',
    lastUpdated: '2025-01-03',
    totalAssessments: 10,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-017', 10),
  },
  {
    id: 'cust-018',
    fullName: 'Adv. Nisha Choudhary',
    pan: 'RSTNC9012E',
    dateOfBirth: '1985-02-14',
    gender: 'female',
    emailPrimary: 'nisha.choudhary.law@gmail.com',
    mobilePrimary: '8109876543',
    residentialAddress: addresses.RJ[1],
    occupation: 'self_employed',
    employerName: 'Choudhary Legal Services',
    annualIncomeRange: '10-25L',
    hasHouseProperty: false,
    hasCapitalGains: false,
    hasBusinessIncome: true,
    previousYearTaxPaid: 175000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2022-03-25',
    lastUpdated: '2025-01-02',
    totalAssessments: 4,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-018', 4),
  },
  {
    id: 'cust-019',
    fullName: 'Dr. Gaurav Khanna',
    pan: 'STUGK3456F',
    dateOfBirth: '1974-06-27',
    gender: 'male',
    emailPrimary: 'dr.gaurav.khanna@gmail.com',
    mobilePrimary: '8098765432',
    residentialAddress: addresses.DL[3],
    occupation: 'self_employed',
    employerName: 'Khanna Orthopedic Centre',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 1820000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2015-07-18',
    lastUpdated: '2025-01-01',
    totalAssessments: 11,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-019', 11),
  },
  {
    id: 'cust-020',
    fullName: 'CA Ritu Bansal',
    pan: 'TUVRB7890G',
    dateOfBirth: '1982-12-08',
    gender: 'female',
    emailPrimary: 'ritu.bansal.ca@gmail.com',
    mobilePrimary: '7987654321',
    residentialAddress: addresses.WB[0],
    occupation: 'self_employed',
    employerName: 'Bansal Audit Firm',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: true,
    previousYearTaxPaid: 540000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2018-10-05',
    lastUpdated: '2024-12-30',
    totalAssessments: 8,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-020', 8),
  },

  // Business Owners (10)
  {
    id: 'cust-021',
    fullName: 'Sunil Kumar Shah',
    pan: 'UVWSS1234H',
    aadhaar: '789012345678',
    dateOfBirth: '1970-03-20',
    gender: 'male',
    emailPrimary: 'sunil.shah@shahenterprises.in',
    mobilePrimary: '7876543210',
    whatsappNumber: '7876543210',
    residentialAddress: addresses.GJ[2],
    occupation: 'business',
    employerName: 'Shah Enterprises Pvt Ltd',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 2450000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2014-05-12',
    lastUpdated: '2025-01-10',
    totalAssessments: 12,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-021', 12),
  },
  {
    id: 'cust-022',
    fullName: 'Lakshmi Narayanan',
    pan: 'VWXLN5678I',
    dateOfBirth: '1968-08-15',
    gender: 'male',
    emailPrimary: 'lakshmi.n@narayanantextiles.com',
    mobilePrimary: '7765432109',
    residentialAddress: addresses.TN[1],
    occupation: 'business',
    employerName: 'Narayanan Textiles',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 3200000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2013-08-20',
    lastUpdated: '2025-01-09',
    totalAssessments: 13,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-022', 13),
  },
  {
    id: 'cust-023',
    fullName: 'Preeti Agarwal',
    pan: 'WXYPA9012J',
    dateOfBirth: '1975-11-28',
    gender: 'female',
    emailPrimary: 'preeti@agarwalfashions.in',
    mobilePrimary: '7654321098',
    residentialAddress: addresses.RJ[2],
    occupation: 'business',
    employerName: 'Agarwal Fashions',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: true,
    previousYearTaxPaid: 720000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2017-02-14',
    lastUpdated: '2025-01-08',
    totalAssessments: 9,
    pendingAssessments: 2,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-023', 9),
  },
  {
    id: 'cust-024',
    fullName: 'Harish Chandra Gupta',
    pan: 'XYZHG3456K',
    dateOfBirth: '1965-04-05',
    gender: 'male',
    emailPrimary: 'harish@guptasteels.com',
    mobilePrimary: '7543210987',
    residentialAddress: addresses.UP[2],
    occupation: 'business',
    employerName: 'Gupta Steels & Alloys',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 4100000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2012-06-30',
    lastUpdated: '2025-01-07',
    totalAssessments: 14,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-024', 14),
  },
  {
    id: 'cust-025',
    fullName: 'Savita Joshi',
    pan: 'YZASJ7890L',
    dateOfBirth: '1978-09-12',
    gender: 'female',
    emailPrimary: 'savita@joshiorganics.in',
    mobilePrimary: '7432109876',
    residentialAddress: addresses.MH[4],
    occupation: 'business',
    employerName: 'Joshi Organic Foods',
    annualIncomeRange: '10-25L',
    hasHouseProperty: false,
    hasCapitalGains: false,
    hasBusinessIncome: true,
    previousYearTaxPaid: 195000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2020-09-18',
    lastUpdated: '2025-01-06',
    totalAssessments: 6,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-025', 6),
  },
  {
    id: 'cust-026',
    fullName: 'Ramakrishna Shetty',
    pan: 'ZABRS1234M',
    dateOfBirth: '1973-12-22',
    gender: 'male',
    emailPrimary: 'ramakrishna@shettyhospitality.com',
    mobilePrimary: '7321098765',
    residentialAddress: addresses.KA[4],
    occupation: 'business',
    employerName: 'Shetty Hospitality Group',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 2850000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2015-03-25',
    lastUpdated: '2025-01-05',
    totalAssessments: 11,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-026', 11),
  },
  {
    id: 'cust-027',
    fullName: 'Manisha Tiwari',
    pan: 'ABCMT5678N',
    dateOfBirth: '1980-07-08',
    gender: 'female',
    emailPrimary: 'manisha@tiwariconstruction.in',
    mobilePrimary: '7210987654',
    residentialAddress: addresses.UP[3],
    occupation: 'business',
    employerName: 'Tiwari Construction Co',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: true,
    previousYearTaxPaid: 680000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2018-11-10',
    lastUpdated: '2025-01-04',
    totalAssessments: 8,
    pendingAssessments: 2,
    overdueAssessments: 1,
    assessments: generateAssessments('cust-027', 8),
  },
  {
    id: 'cust-028',
    fullName: 'Prakash Chand Mittal',
    pan: 'BCDPM9012O',
    dateOfBirth: '1962-02-18',
    gender: 'male',
    emailPrimary: 'prakash@mittaltraders.com',
    mobilePrimary: '7109876543',
    residentialAddress: addresses.DL[4],
    occupation: 'business',
    employerName: 'Mittal Traders & Sons',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 3650000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2011-04-20',
    lastUpdated: '2025-01-03',
    totalAssessments: 15,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-028', 15),
  },
  {
    id: 'cust-029',
    fullName: 'Geeta Devi Agrawal',
    pan: 'CDEGA3456P',
    dateOfBirth: '1971-10-30',
    gender: 'female',
    emailPrimary: 'geeta@agrawalexports.in',
    mobilePrimary: '7098765432',
    residentialAddress: addresses.WB[1],
    occupation: 'business',
    employerName: 'Agrawal Exports',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 820000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2016-07-15',
    lastUpdated: '2025-01-02',
    totalAssessments: 10,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-029', 10),
  },
  {
    id: 'cust-030',
    fullName: 'Venkat Subramanian',
    pan: 'DEFVS7890Q',
    dateOfBirth: '1969-05-25',
    gender: 'male',
    emailPrimary: 'venkat@subramanianfoods.com',
    mobilePrimary: '9987654321',
    residentialAddress: addresses.TN[2],
    occupation: 'business',
    employerName: 'Subramanian Foods Ltd',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: true,
    previousYearTaxPaid: 2980000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2014-01-08',
    lastUpdated: '2025-01-01',
    totalAssessments: 12,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-030', 12),
  },

  // Retired (5)
  {
    id: 'cust-031',
    fullName: 'Rtd. Col. Mahendra Singh',
    pan: 'EFGMS1234R',
    aadhaar: '890123456789',
    dateOfBirth: '1958-06-15',
    gender: 'male',
    emailPrimary: 'col.mahendra.singh@gmail.com',
    mobilePrimary: '9876123450',
    residentialAddress: addresses.UP[4],
    occupation: 'retired',
    annualIncomeRange: '10-25L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 125000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2019-04-10',
    lastUpdated: '2024-12-28',
    totalAssessments: 7,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-031', 7),
  },
  {
    id: 'cust-032',
    fullName: 'Kamala Devi Sharma',
    pan: 'FGHKD5678S',
    dateOfBirth: '1955-12-03',
    gender: 'female',
    emailPrimary: 'kamala.sharma55@gmail.com',
    mobilePrimary: '9765123480',
    residentialAddress: addresses.RJ[3],
    occupation: 'retired',
    annualIncomeRange: '5-10L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 45000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2020-06-22',
    lastUpdated: '2024-12-27',
    totalAssessments: 6,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-032', 6),
  },
  {
    id: 'cust-033',
    fullName: 'Rtd. IAS R. Krishnamurthy',
    pan: 'GHIRK9012T',
    dateOfBirth: '1960-09-18',
    gender: 'male',
    emailPrimary: 'r.krishnamurthy.ias@gmail.com',
    mobilePrimary: '9654123470',
    residentialAddress: addresses.TN[3],
    occupation: 'retired',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 380000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2018-03-15',
    lastUpdated: '2024-12-26',
    totalAssessments: 8,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-033', 8),
  },
  {
    id: 'cust-034',
    fullName: 'Shakuntala Bai Patil',
    pan: 'HIJSP3456U',
    dateOfBirth: '1952-04-28',
    gender: 'female',
    emailPrimary: 'shakuntala.patil52@gmail.com',
    mobilePrimary: '9543123460',
    residentialAddress: addresses.MH[1],
    occupation: 'retired',
    annualIncomeRange: '5-10L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 35000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2021-08-30',
    lastUpdated: '2024-12-25',
    totalAssessments: 5,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-034', 5),
  },
  {
    id: 'cust-035',
    fullName: 'Rtd. Prof. A.K. Mukherjee',
    pan: 'IJKAM7890V',
    dateOfBirth: '1957-07-12',
    gender: 'male',
    emailPrimary: 'prof.mukherjee@gmail.com',
    mobilePrimary: '9432123450',
    residentialAddress: addresses.WB[2],
    occupation: 'retired',
    annualIncomeRange: '10-25L',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 165000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2017-11-20',
    lastUpdated: '2024-12-24',
    totalAssessments: 9,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-035', 9),
  },

  // Salaried - Other Sectors (10)
  {
    id: 'cust-036',
    fullName: 'Rohit Malhotra',
    pan: 'JKLRM1234W',
    dateOfBirth: '1989-01-25',
    gender: 'male',
    emailPrimary: 'rohit.malhotra@icicibank.com',
    mobilePrimary: '9321123440',
    residentialAddress: addresses.MH[0],
    occupation: 'salaried',
    employerName: 'ICICI Bank',
    annualIncomeRange: '10-25L',
    hasHouseProperty: false,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 145000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2022-02-18',
    lastUpdated: '2024-12-23',
    totalAssessments: 4,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-036', 4),
  },
  {
    id: 'cust-037',
    fullName: 'Anjali Verma',
    pan: 'KLMAV5678X',
    dateOfBirth: '1993-05-08',
    gender: 'female',
    emailPrimary: 'anjali.verma@hdfc.com',
    mobilePrimary: '9210123430',
    residentialAddress: addresses.DL[1],
    occupation: 'salaried',
    employerName: 'HDFC Ltd',
    annualIncomeRange: '5-10L',
    hasHouseProperty: false,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 65000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2023-04-12',
    lastUpdated: '2024-12-22',
    totalAssessments: 3,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-037', 3),
  },
  {
    id: 'cust-038',
    fullName: 'Karthik Rajan',
    pan: 'LMNKR9012Y',
    dateOfBirth: '1986-11-16',
    gender: 'male',
    emailPrimary: 'karthik.rajan@airtel.com',
    mobilePrimary: '9109123420',
    residentialAddress: addresses.TN[4],
    occupation: 'salaried',
    employerName: 'Bharti Airtel',
    annualIncomeRange: '10-25L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 175000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2021-07-25',
    lastUpdated: '2024-12-21',
    totalAssessments: 5,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-038', 5),
  },
  {
    id: 'cust-039',
    fullName: 'Sunita Rani',
    pan: 'MNOSR3456Z',
    dateOfBirth: '1988-08-22',
    gender: 'female',
    emailPrimary: 'sunita.rani@relianceindustries.com',
    mobilePrimary: '8998123410',
    residentialAddress: addresses.GJ[3],
    occupation: 'salaried',
    employerName: 'Reliance Industries',
    annualIncomeRange: '25-50L',
    hasHouseProperty: false,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 420000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2020-10-08',
    lastUpdated: '2024-12-20',
    totalAssessments: 6,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-039', 6),
  },
  {
    id: 'cust-040',
    fullName: 'Nitin Deshmukh',
    pan: 'NOPND7890A',
    dateOfBirth: '1984-03-14',
    gender: 'male',
    emailPrimary: 'nitin.deshmukh@tatasteel.com',
    mobilePrimary: '8887123400',
    residentialAddress: addresses.MH[2],
    occupation: 'salaried',
    employerName: 'Tata Steel',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 365000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2019-05-30',
    lastUpdated: '2024-12-19',
    totalAssessments: 7,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-040', 7),
  },
  {
    id: 'cust-041',
    fullName: 'Pallavi Hegde',
    pan: 'OPQPH1234B',
    dateOfBirth: '1991-09-05',
    gender: 'female',
    emailPrimary: 'pallavi.hegde@mindtree.com',
    mobilePrimary: '8776123390',
    residentialAddress: addresses.KA[2],
    occupation: 'salaried',
    employerName: 'LTIMindtree',
    annualIncomeRange: '10-25L',
    hasHouseProperty: false,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 195000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2022-06-15',
    lastUpdated: '2024-12-18',
    totalAssessments: 4,
    pendingAssessments: 2,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-041', 4),
  },
  {
    id: 'cust-042',
    fullName: 'Ajay Kumar Singh',
    pan: 'PQRAS5678C',
    dateOfBirth: '1979-12-28',
    gender: 'male',
    emailPrimary: 'ajay.singh@ongc.co.in',
    mobilePrimary: '8665123380',
    residentialAddress: addresses.GJ[4],
    occupation: 'salaried',
    employerName: 'ONGC',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 485000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2018-09-12',
    lastUpdated: '2024-12-17',
    totalAssessments: 8,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-042', 8),
  },
  {
    id: 'cust-043',
    fullName: 'Divya Pillai',
    pan: 'QRSDP9012D',
    dateOfBirth: '1994-06-18',
    gender: 'female',
    emailPrimary: 'divya.pillai@zoho.com',
    mobilePrimary: '8554123370',
    residentialAddress: addresses.TN[0],
    occupation: 'salaried',
    employerName: 'Zoho Corporation',
    annualIncomeRange: '5-10L',
    hasHouseProperty: false,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 55000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2023-01-20',
    lastUpdated: '2024-12-16',
    totalAssessments: 3,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-043', 3),
  },
  {
    id: 'cust-044',
    fullName: 'Manoj Kumar Pandey',
    pan: 'RSTMP3456E',
    dateOfBirth: '1981-02-09',
    gender: 'male',
    emailPrimary: 'manoj.pandey@sbi.co.in',
    mobilePrimary: '8443123360',
    residentialAddress: addresses.UP[0],
    occupation: 'salaried',
    employerName: 'State Bank of India',
    annualIncomeRange: '10-25L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 185000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2020-04-28',
    lastUpdated: '2024-12-15',
    totalAssessments: 6,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-044', 6),
  },
  {
    id: 'cust-045',
    fullName: 'Rashmi Kulkarni',
    pan: 'STURK7890F',
    dateOfBirth: '1987-10-14',
    gender: 'female',
    emailPrimary: 'rashmi.kulkarni@hcl.com',
    mobilePrimary: '8332123350',
    residentialAddress: addresses.KA[0],
    occupation: 'salaried',
    employerName: 'HCL Technologies',
    annualIncomeRange: '10-25L',
    hasHouseProperty: false,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 165000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2021-11-18',
    lastUpdated: '2024-12-14',
    totalAssessments: 5,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-045', 5),
  },

  // Inactive/Archived Customers (5)
  {
    id: 'cust-046',
    fullName: 'Vikas Sharma',
    pan: 'TUVVS1234G',
    dateOfBirth: '1983-07-20',
    gender: 'male',
    emailPrimary: 'vikas.sharma.old@gmail.com',
    mobilePrimary: '8221123340',
    residentialAddress: addresses.DL[0],
    occupation: 'salaried',
    employerName: 'Previous Employer',
    annualIncomeRange: '10-25L',
    hasHouseProperty: false,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 125000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'inactive',
    createdDate: '2018-03-10',
    lastUpdated: '2023-08-15',
    totalAssessments: 5,
    pendingAssessments: 0,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-046', 5),
  },
  {
    id: 'cust-047',
    fullName: 'Rekha Kumari',
    pan: 'UVWRK5678H',
    dateOfBirth: '1975-04-12',
    gender: 'female',
    emailPrimary: 'rekha.kumari75@gmail.com',
    mobilePrimary: '8110123330',
    residentialAddress: addresses.RJ[4],
    occupation: 'retired',
    annualIncomeRange: '5-10L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 35000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'archived',
    createdDate: '2016-06-25',
    lastUpdated: '2022-12-30',
    totalAssessments: 7,
    pendingAssessments: 0,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-047', 7),
  },
  {
    id: 'cust-048',
    fullName: 'Sudhir Thakur',
    pan: 'VWXST9012I',
    dateOfBirth: '1970-11-08',
    gender: 'male',
    emailPrimary: 'sudhir.thakur70@gmail.com',
    mobilePrimary: '8009123320',
    residentialAddress: addresses.MH[3],
    occupation: 'business',
    employerName: 'Thakur Traders (Closed)',
    annualIncomeRange: '10-25L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 145000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'inactive',
    createdDate: '2015-09-18',
    lastUpdated: '2023-04-20',
    totalAssessments: 8,
    pendingAssessments: 0,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-048', 8),
  },
  {
    id: 'cust-049',
    fullName: 'Lakshmi Bai',
    pan: 'WXYLB3456J',
    dateOfBirth: '1960-08-25',
    gender: 'female',
    emailPrimary: 'lakshmi.bai60@gmail.com',
    mobilePrimary: '7998123310',
    residentialAddress: addresses.TG[4],
    occupation: 'retired',
    annualIncomeRange: '0-5L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 0,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'archived',
    createdDate: '2017-02-28',
    lastUpdated: '2022-07-15',
    totalAssessments: 6,
    pendingAssessments: 0,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-049', 6),
  },
  {
    id: 'cust-050',
    fullName: 'Ramesh Chand Yadav',
    pan: 'XYZRY7890K',
    dateOfBirth: '1968-01-15',
    gender: 'male',
    emailPrimary: 'ramesh.yadav68@gmail.com',
    mobilePrimary: '7887123300',
    residentialAddress: addresses.UP[1],
    occupation: 'self_employed',
    employerName: 'Yadav Consultancy (Closed)',
    annualIncomeRange: '5-10L',
    hasHouseProperty: true,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 45000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'inactive',
    createdDate: '2014-11-10',
    lastUpdated: '2023-06-30',
    totalAssessments: 10,
    pendingAssessments: 0,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-050', 10),
  },

  // Additional customers to reach 55+
  {
    id: 'cust-051',
    fullName: 'Ashok Kumar Mishra',
    pan: 'YZAAM1234L',
    dateOfBirth: '1980-05-22',
    gender: 'male',
    emailPrimary: 'ashok.mishra@gmail.com',
    mobilePrimary: '9876512340',
    residentialAddress: addresses.KA[1],
    occupation: 'salaried',
    employerName: 'IBM India',
    annualIncomeRange: '25-50L',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 520000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2019-08-15',
    lastUpdated: '2025-01-10',
    totalAssessments: 7,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-051', 7),
  },
  {
    id: 'cust-052',
    fullName: 'Poornima Rao',
    pan: 'ZABPR5678M',
    dateOfBirth: '1986-09-30',
    gender: 'female',
    emailPrimary: 'poornima.rao@deloitte.com',
    mobilePrimary: '9765412380',
    residentialAddress: addresses.TG[1],
    occupation: 'salaried',
    employerName: 'Deloitte India',
    annualIncomeRange: '25-50L',
    hasHouseProperty: false,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 450000,
    assignedCA: 'ca-003',
    assignedCAName: 'Amit Patel',
    status: 'active',
    createdDate: '2020-02-20',
    lastUpdated: '2025-01-09',
    totalAssessments: 6,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-052', 6),
  },
  {
    id: 'cust-053',
    fullName: 'Vijay Shankar Dubey',
    pan: 'ABCVD9012N',
    dateOfBirth: '1977-12-08',
    gender: 'male',
    emailPrimary: 'vijay.dubey@pwc.com',
    mobilePrimary: '9654312470',
    residentialAddress: addresses.MH[1],
    occupation: 'salaried',
    employerName: 'PwC India',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 980000,
    assignedCA: 'ca-004',
    assignedCAName: 'Neha Gupta',
    status: 'active',
    createdDate: '2018-05-12',
    lastUpdated: '2025-01-08',
    totalAssessments: 8,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-053', 8),
  },
  {
    id: 'cust-054',
    fullName: 'Shalini Bhatt',
    pan: 'BCDSB3456O',
    dateOfBirth: '1990-03-15',
    gender: 'female',
    emailPrimary: 'shalini.bhatt@kpmg.com',
    mobilePrimary: '9543212560',
    residentialAddress: addresses.DL[2],
    occupation: 'salaried',
    employerName: 'KPMG India',
    annualIncomeRange: '10-25L',
    hasHouseProperty: false,
    hasCapitalGains: false,
    hasBusinessIncome: false,
    previousYearTaxPaid: 175000,
    assignedCA: 'ca-001',
    assignedCAName: 'Rajesh Kumar',
    status: 'active',
    createdDate: '2021-09-28',
    lastUpdated: '2025-01-07',
    totalAssessments: 5,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-054', 5),
  },
  {
    id: 'cust-055',
    fullName: 'Anand Krishnan',
    pan: 'CDEAK7890P',
    dateOfBirth: '1982-07-25',
    gender: 'male',
    emailPrimary: 'anand.krishnan@ey.com',
    mobilePrimary: '9432112650',
    residentialAddress: addresses.KL[2],
    occupation: 'salaried',
    employerName: 'EY India',
    annualIncomeRange: '50L+',
    hasHouseProperty: true,
    hasCapitalGains: true,
    hasBusinessIncome: false,
    previousYearTaxPaid: 1150000,
    assignedCA: 'ca-002',
    assignedCAName: 'Priya Sharma',
    status: 'active',
    createdDate: '2017-10-15',
    lastUpdated: '2025-01-06',
    totalAssessments: 9,
    pendingAssessments: 1,
    overdueAssessments: 0,
    assessments: generateAssessments('cust-055', 9),
  },
]

// ============================================================================
// HELPER FUNCTIONS FOR MOCK DATA
// ============================================================================

export function getCustomerById(id: string): IndividualCustomer | undefined {
  return mockCustomers.find(c => c.id === id)
}

export function getCustomersByCA(caId: string): IndividualCustomer[] {
  return mockCustomers.filter(c => c.assignedCA === caId)
}

export function getActiveCustomers(): IndividualCustomer[] {
  return mockCustomers.filter(c => c.status === 'active')
}

export function getCustomerStats() {
  const total = mockCustomers.length
  const active = mockCustomers.filter(c => c.status === 'active').length
  const inactive = mockCustomers.filter(c => c.status === 'inactive').length
  const archived = mockCustomers.filter(c => c.status === 'archived').length
  const withPending = mockCustomers.filter(c => c.pendingAssessments > 0).length
  const withOverdue = mockCustomers.filter(c => c.overdueAssessments > 0).length

  return { total, active, inactive, archived, withPending, withOverdue }
}
